import './locale/es';
import 'moment/locale/en-gb';
import 'moment/locale/pt';
import React, { useState, useRef, useEffect } from 'react';
import { Typography, FormHelperText, InputAdornment } from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import { useField } from 'formik';
import { DatePicker as DatePickerUI } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { getCookie } from '../../shared/helpers/cookies';
import { useFocusOnError } from '../../shared/hooks';
import dateIcon from '../../assets/icons/ico-date.png';
import getStyles from './style';

const DatePicker = ({
  name,
  label,
  placeholder,
  value,
  disabled,
  disableIcon,
  min,
  max,
  labelGrey = false,
  ...props
}) => {
  const theme = useTheme();
  const classes = getStyles(theme);
  const [changed, setChanged] = useState(true);
  const [field, meta, fieldProps] = useField(name);
  const idiom = getCookie('idiom');
  const localeCalendar =
    idiom === 'es_ES' ? 'es' : idiom === 'en_US' ? 'en-gb' : 'pt';
  const [dtopen, setDtOpen] = useState(false);

  const handleChange = (e) => {
    if (!changed) setChanged(true);
    fieldProps.setValue(e ? moment(moment(e).format('YYYY-MM-DD')) : null);
  };

  const calCancelLabel =
    idiom === 'es_ES' ? 'Cancelar' : idiom === 'en_US' ? 'Cancel' : 'Cancelar';
  moment.locale(localeCalendar);

  useEffect(() => {
    if (field.value && changed) {
      queueMicrotask(() => {
        handleChange(field.value);
        setChanged(false);
      });
    }
  }, [field?.value]);

  const fieldRef = useRef(null);
  useFocusOnError({ fieldName: name, fieldId: `form-field-${name}` });

  return (
    <>
      <InvisibleButton
        data-testid={`input-${name}`}
        type="button"
        onClick={() => handleChange(moment())}
      />
      {label && (
        <Typography
          sx={{
            ...classes.title,
            ...(labelGrey ? classes.titleColor : {}),
          }}
        >
          {label}
        </Typography>
      )}
      <LocalizationProvider
        dateAdapter={AdapterMoment}
        adapterLocale={localeCalendar}
      >
        <DatePickerUI
          {...props}
          enableAccessibleFieldDOMStructure={false}
          label=""
          value={field?.value ? moment(field.value) : null}
          inputRef={fieldRef}
          format="DD/MM/YYYY"
          maxDate={max}
          minDate={min || moment().year(1900).month(0).date(1)}
          disabled={disabled}
          onChange={handleChange}
          open={disableIcon ? false : dtopen}
          onClose={() => setDtOpen(false)}
          slotProps={{
            textField: {
              id: `form-field-${name}`,
              placeholder,
              variant: 'outlined',
              fullWidth: true,
              inputProps: {
                'data-testid': `input-datepicker-${name}`,
              },
              error: meta.touched && !!meta.error,
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon
                      src={dateIcon}
                      alt="date-icon"
                      onClick={() => setDtOpen(true)}
                    />
                  </InputAdornment>
                ),
              },
              sx: {
                '& .MuiOutlinedInput-root': {
                  ...classes.inputLightRoot,
                  '& .MuiOutlinedInput-input': classes.inputOutlined,
                  '& .MuiOutlinedInput-notchedOutline': classes.notchedOutline,
                  '&.Mui-error': {
                    border: '1px solid #f44336',
                  },
                },
                '& .MuiInputLabel-root': {
                  display: 'none',
                },
              },
            },
            actionBar: {
              actions: ['cancel', 'accept'],
            },
            openPickerButton: {
              sx: { display: 'none' },
            },
          }}
          cancelLabel={calCancelLabel}
        />
      </LocalizationProvider>
      {meta.touched && meta?.error && (
        <CustomHelperText error>{meta.error}</CustomHelperText>
      )}
    </>
  );
};

const CustomHelperText = styled(FormHelperText)({
  marginLeft: 14,
  marginRight: 14,
});

export const InvisibleButton = styled('button')({
  display: 'none',
});

export const Icon = styled('img')({
  width: 24,
  cursor: 'pointer',
});

export default DatePicker;
